import React, { useMemo } from "react";
import { Box, Card, Typography, Stack } from "@mui/material";
import refreshIcon from "../../assets/WidgetsIcon/refresh.svg";
import clockIcon from "../../assets/WidgetsIcon/time.svg";
import timerIcon from "../../assets/WidgetsIcon/timer.svg";
import curveArrowGreenDown from "../../assets/WidgetsIcon/curve-arrow-green-down.svg";
import curveArrowRedUp from "../../assets/WidgetsIcon/curve-arrow-red-up.svg";

function convertSecondsObjectToTimeFormat(obj) {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (value === null || value < 0) {
        return [key, "0:00"];
      }

      const roundedValue = Math.round(value);
      const days = Math.floor(roundedValue / 86400);
      const hours = Math.floor((roundedValue % 86400) / 3600);
      const minutes = Math.floor((roundedValue % 3600) / 60);
      const seconds = roundedValue % 60;

      let formattedTime = "";

      if (days > 0) {
        formattedTime = `${days}:${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
      } else if (hours > 0) {
        formattedTime = `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
      } else {
        formattedTime = `${minutes}:${seconds.toString().padStart(2, "0")}`;
      }

      return [key, formattedTime];
    })
  );
}

function calculatePercentageChange(response, responsePrev) {
  return Object.fromEntries(
    Object.entries(response).map(([key, value]) => {
      const prevValue = responsePrev[key];

      const validPrevValue = prevValue !== undefined && prevValue >= 0 ? prevValue : 0;

      if (value === null || prevValue === null) {
        return [key, 0];
      }

      if (validPrevValue === 0) {
        return [key, 0];
      }

      const percentageChange = ((value - validPrevValue) / validPrevValue) * 100;
      return [key, parseFloat(percentageChange.toFixed(1))];
    })
  );
}

const ResponseEfficiency = ({ dateRange, data }) => {
  const formattedTime = useMemo(() => convertSecondsObjectToTimeFormat(data.response), [data]);
  const percentageChange = useMemo(() => calculatePercentageChange(data.response, data.responsePrev), [data]);

  return (
    <Card
      className="p-3"
      sx={{
        boxShadow: "0px 4px 4px 0px #0000000F",
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            marginLeft: 1,
            fontWeight: 700,
            lineHeight: "20px",
          }}
        >
          Response Efficiency
        </Typography>
      </Stack>
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Box
          className="d-flex flex-column justify-content-between"
          sx={{
            px: 2,
            py: "12px",
            border: "1px solid #E1E1E1",
            borderRadius: "8px",
            height: 171,
            minWidth: 640,
            flex: 1,
          }}
        >
          <Box className="d-flex">
            <img src={timerIcon} alt="noticeIcon" />{" "}
            <Typography
              variant="h6"
              sx={{
                marginLeft: 1,
                fontWeight: 700,
                lineHeight: "20px",
              }}
            >
              Response Times
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={2}
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                py: 2,
                minWidth: "130px",
                pb: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                flex: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Average
              </Typography>

              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 700,
                }}
              >
                {formattedTime.average}
              </Typography>

              <Box className="d-flex">
                <img
                  src={percentageChange.average <= 0 ? curveArrowGreenDown : curveArrowRedUp}
                  alt="curveArrowGreenDown"
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: "#00000091",
                  }}
                >
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      color: percentageChange.average <= 0 ? "#45856B" : "#C71818",
                      fontWeight: 600,
                      mx: "5px",
                    }}
                  >
                    {percentageChange.average + "%"}
                  </Typography>
                  previous {dateRange} days
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                py: 2,
                minWidth: "130px",
                pb: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                flex: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#45856B",
                }}
              >
                Best
              </Typography>

              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#45856B",
                }}
              >
                {formattedTime.best}
              </Typography>

              <Box className="d-flex">
                <img
                  src={percentageChange.best <= 0 ? curveArrowGreenDown : curveArrowRedUp}
                  alt="curveArrowGreenDown"
                />

                <Typography
                  variant="body2"
                  sx={{
                    color: "#00000091",
                  }}
                >
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      color: percentageChange.best <= 0 ? "#45856B" : "#C71818",
                      fontWeight: 600,
                      mx: "5px",
                    }}
                  >
                    {percentageChange.best + "%"}
                  </Typography>
                  previous {dateRange} days
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                py: 2,
                minWidth: "130px",
                pb: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                flex: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#C71818",
                }}
              >
                Worst
              </Typography>

              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#C71818",
                }}
              >
                {formattedTime.worst}
              </Typography>

              <Box className="d-flex">
                <img
                  src={percentageChange.worst <= 0 ? curveArrowGreenDown : curveArrowRedUp}
                  alt="curveArrowGreenDown"
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: "#00000091",
                  }}
                >
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      color: percentageChange.worst <= 0 ? "#45856B" : "#C71818",
                      fontWeight: 600,
                      mx: "5px",
                    }}
                  >
                    {percentageChange.worst + "%"}
                  </Typography>
                  previous {dateRange} days
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>

        <Box
          className="d-flex flex-column justify-content-between"
          sx={{
            px: 2,
            py: "12px",
            border: "1px solid #E1E1E1",
            borderRadius: "8px",
            height: 170,
            minWidth: 317,
            // maxWidth: 457,
            flex: 1,
          }}
        >
          <Box className="d-flex">
            <img src={clockIcon} alt="noticeIcon" />{" "}
            <Typography
              variant="h6"
              sx={{
                marginLeft: 1,
                fontWeight: 700,
                lineHeight: "25px",
              }}
            >
              Average Time to First Arriving Unit
            </Typography>
          </Box>
          <Box className="d-flex align-items-baseline mt-auto mb-2">
            <Typography
              variant="h4"
              sx={{
                marginRight: 1,
                fontWeight: 700,
              }}
            >
              {formattedTime.average}
            </Typography>
            <img
              src={percentageChange.average <= 0 ? curveArrowGreenDown : curveArrowRedUp}
              alt="curveArrow"
              className="me-1"
            />
            <Typography
              variant="body2"
              sx={{ color: percentageChange.average <= 0 ? "#45856B" : "#C71818", fontWeight: 600 }}
              className="me-2"
            >
              {percentageChange.average + "%"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#00000078" }}>
              previous {dateRange} days
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: "#00000091",
            }}
          >
            51% of incidents
          </Typography>
        </Box>

        <Box
          className="d-flex flex-column justify-content-between"
          sx={{
            px: 2,
            py: "12px",
            border: "1px solid #E1E1E1",
            borderRadius: "8px",
            height: 170,
            minWidth: 317,
            // maxWidth: 500,
            flex: 1,
          }}
        >
          <Box className="d-flex">
            <img src={refreshIcon} alt="noticeIcon" />{" "}
            <Typography
              variant="h6"
              sx={{
                marginLeft: 1,
                fontWeight: 700,
                lineHeight: "20px",
              }}
            >
              Mutual Aid Responses
            </Typography>
          </Box>
          <Box className="d-flex align-items-baseline mt-auto mb-2">
            <Typography
              variant="h4"
              sx={{
                marginRight: 1,
                fontWeight: 700,
              }}
            >
              17
            </Typography>
            <img src={curveArrowGreenDown} alt="curveArrow" className="me-1" />
            <Typography variant="body2" sx={{ color: "#45856B", fontWeight: 600 }} className="me-2">
              12%
            </Typography>
            <Typography variant="body2" sx={{ color: "#00000078" }}>
              previous {dateRange} days
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: "#00000091",
            }}
          >
            12% of incidents
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
};

export default ResponseEfficiency;
